import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { getCurrentUser, getUsersByRole } from "../../../services/api/core";
import {
  getModelReferenceData,
  getWorkScopes,
} from "../../../services/api/dispatch";
import {
  createPlanningSyncEvent,
  getPlanningLastUpdated,
} from "../../../services/api/planningSync";
import { useNotificationQueue } from "../../../hooks/useNotificationQueue";
import { runPlanningOperation as runOperationCore } from "../../../features/planning/operations/planningOperationRunner";
import {
  getPlanningOperationMessage,
  shouldNotifyPlanningOperation,
} from "../../../config";
import { getUserDisplayName } from "../../../utils/business";

/**
 * Shared planning reference data for Dispatch, Schedule, and other /planning routes.
 *
 * @context PlanningContext
 */
export const PlanningContext = createContext(null);

function createClientInstanceId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `planning-client-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Final UX pass for planning notification text.
 *
 * Keep this as the last message transform before a toast/sync row is created so
 * future operation-specific detail mutations can run on the plain message first.
 * @param {string} message
 * @returns {string}
 */
function formatPlanningNotificationMessage(message) {
  if (!message) {
    return message;
  }
  return `${message.charAt(0).toUpperCase()}${message.slice(1)}`;
}

/**
 * Format a planning sync-log row for the shared Last Modified display.
 *
 * @param {Object|null|undefined} event Planning sync-log row.
 * @returns {string}
 */
function formatPlanningLastUpdated(event) {
  if (!event) {
    return "";
  }
  const timestamp = event.created_at ?? event.createdAt;
  if (!timestamp) {
    return "";
  }
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const actorName = event.actor_name ?? event.actorName ?? "Unknown user";
  const datePart = new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  }).format(date).replace(/\//g, "-");
  const timePart = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
  return `Last modified by ${actorName || "Unknown user"} on ${datePart}, ${timePart}`;
}

export function usePlanningContext() {
  const context = useContext(PlanningContext);
  if (!context) {
    throw new Error(
      "usePlanningContext must be used within a PlanningProvider"
    );
  }
  return context;
}

export function PlanningProvider({ children }) {
  const {
    items: notificationItems,
    push: notify,
    dismiss: dismissNotification,
  } = useNotificationQueue();
  const [scopeObjects, setScopeObjects] = useState(null);
  const [isScopeObjectsLoading, setIsScopeObjectsLoading] = useState(true);

  const [workers, setWorkers] = useState(null);
  const [isWorkersLoading, setIsWorkersLoading] = useState(true);

  const [modelReferenceData, setModelReferenceData] = useState(null);
  const [isModelReferenceLoading, setIsModelReferenceLoading] =
    useState(false);
  const [planningOperationEvents, setPlanningOperationEvents] = useState([]);
  const [lastSyncCursor, setLastSyncCursor] = useState(null);
  const [planningUser, setPlanningUser] = useState(null);
  const [planningLastUpdated, setPlanningLastUpdated] = useState("");
  const opSeqRef = useRef(0);
  const clientInstanceIdRef = useRef(createClientInstanceId());

  const setPlanningLastUpdatedFromEvent = useCallback((event) => {
    const formatted = formatPlanningLastUpdated(event);
    if (formatted) {
      setPlanningLastUpdated(formatted);
    }
  }, []);

  /**
   * Append one planning operation event to the in-memory local log.
   *
   * This is a frontend-only placeholder for sync metadata. It keeps a bounded
   * rolling list and updates `lastSyncCursor` to the newest event timestamp so
   * future polling/merge logic can request events after that cursor.
   *
   * @param {Object} event
   * @returns {Object} The normalized event row that was recorded.
   */
  const recordPlanningOperationEvent = useCallback((event) => {
    opSeqRef.current += 1;
    const next = {
      id: `planning-op-${Date.now()}-${opSeqRef.current}`,
      createdAt: new Date().toISOString(),
      ...event,
    };
    setPlanningOperationEvents((prev) => [...prev.slice(-199), next]);
    setLastSyncCursor(next.createdAt);
    return next;
  }, []);

  /**
   * Unified mutation wrapper for Planning domain actions (Schedule/Dispatch).
   *
   * Runs the provided async operation through shared notification behavior, then
   * records lightweight sync metadata for later cross-module sync integration.
   * API services remain pure; this wrapper lives at the React context boundary.
   *
   * @param {Object} params
   * @param {'schedule'|'dispatch'|string} [params.module]
   * @param {string} [params.operation]
   * @param {Array<string>} [params.resources]
   * @param {Array<string|number>} [params.entityIds]
   * @param {Object} [params.metadata] Extra sync-log metadata.
   * @param {() => Promise<any>} params.apiCall
   * @param {(result: any) => void|Promise<void>} [params.onSuccess]
   * @returns {Promise<any>}
   */
  const runPlanningOperation = useCallback(
    async ({
      module,
      operation,
      resources,
      entityIds,
      metadata,
      ...config
    }) => {
      // Normalize caller metadata so local traces and backend sync rows
      // always use a consistent shape.
      const normalizedModule = module ?? "planning";
      const normalizedOperation = operation ?? "unknown";
      const normalizedResources = Array.isArray(resources) ? resources : [];
      const normalizedEntityIds = Array.isArray(entityIds) ? entityIds : [];
      const syncMetadata = {
        ...(metadata && typeof metadata === "object" ? metadata : {}),
        client_instance_id: clientInstanceIdRef.current,
      };
      const baseMessage = getPlanningOperationMessage(
        normalizedOperation,
        normalizedModule
      );
      const message = formatPlanningNotificationMessage(baseMessage);
      const result = await runOperationCore({
        ...config,
      });
      if (shouldNotifyPlanningOperation(normalizedOperation, normalizedModule)) {
        notify(message, "success");
      }
      const localEvent = recordPlanningOperationEvent({
        module: normalizedModule,
        operation: normalizedOperation,
        resources: normalizedResources,
        entityIds: normalizedEntityIds,
      });
      try {
        const syncEvent = await createPlanningSyncEvent({
          source_module: normalizedModule,
          operation: normalizedOperation,
          resources: normalizedResources,
          entity_ids: normalizedEntityIds,
          notification_message: message,
          actor_name: planningUser?.fullName,
          occurred_at: new Date().toISOString(),
          metadata: syncMetadata,
        });
        const serverCursor = syncEvent?.created_at ?? syncEvent?.createdAt;
        if (serverCursor) {
          setLastSyncCursor(serverCursor);
        }
        setPlanningLastUpdatedFromEvent(syncEvent);
      } catch (syncLogError) {
        console.error("Failed to create planning sync log event", syncLogError);
        setLastSyncCursor(localEvent.createdAt);
        setPlanningLastUpdatedFromEvent({
          created_at: localEvent.createdAt,
          actor_name: planningUser?.fullName || "Unknown user",
        });
      }
      return result;
    },
    [notify, planningUser, recordPlanningOperationEvent, setPlanningLastUpdatedFromEvent]
  );

  useEffect(() => {
    let cancelled = false;
    getPlanningLastUpdated()
      .then((event) => {
        if (!cancelled) {
          setPlanningLastUpdatedFromEvent(event);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          console.error("could not get planning last modified", e);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [setPlanningLastUpdatedFromEvent]);

  useEffect(() => {
    // Cache current user display name once for outgoing sync events.
    let cancelled = false;
    getCurrentUser()
      .then((res) => {
        if (cancelled) {
          return;
        }
        const user = res?.user ?? null;
        if (!user) {
          setPlanningUser(null);
          return;
        }
        setPlanningUser({
          id: user.id ?? null,
          fullName: getUserDisplayName(user, "Unknown user"),
        });
      })
      .catch((e) => {
        if (!cancelled) {
          console.error("could not get planning user", e);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsScopeObjectsLoading(true);
    getWorkScopes()
      .then((data) => {
        if (!cancelled) {
          setScopeObjects(data);
        }
      })
      .catch((e) => console.error("could not get work scopes", e))
      .finally(() => {
        if (!cancelled) {
          setIsScopeObjectsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsWorkersLoading(true);
    getUsersByRole({ active: true })
      .then((data) => {
        if (!cancelled) {
          setWorkers(data);
        }
      })
      .catch((e) => console.error("could not get active users data", e))
      .finally(() => {
        if (!cancelled) {
          setIsWorkersLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const reloadModelReferenceData = useCallback(async () => {
    setIsModelReferenceLoading(true);
    try {
      const data = await getModelReferenceData();
      setModelReferenceData(data);
    } catch (e) {
      console.error("could not get model reference data", e);
    } finally {
      setIsModelReferenceLoading(false);
    }
  }, []);

  const isPlanningDataLoading =
    isScopeObjectsLoading || isWorkersLoading || isModelReferenceLoading;

  const value = useMemo(
    () => ({
      scopeObjects,
      workers,
      modelReferenceData,
      notificationItems,
      dismissNotification,
      notify,
      runPlanningOperation,
      planningOperationEvents,
      lastSyncCursor,
      planningLastUpdated,
      setPlanningLastUpdatedFromEvent,
      planningUser,
      clientInstanceId: clientInstanceIdRef.current,
      recordPlanningOperationEvent,
      reloadModelReferenceData,
      isScopeObjectsLoading,
      isWorkersLoading,
      isModelReferenceLoading,
      isPlanningDataLoading,
    }),
    [
      scopeObjects,
      workers,
      modelReferenceData,
      notificationItems,
      dismissNotification,
      notify,
      runPlanningOperation,
      planningOperationEvents,
      lastSyncCursor,
      planningLastUpdated,
      setPlanningLastUpdatedFromEvent,
      planningUser,
      recordPlanningOperationEvent,
      reloadModelReferenceData,
      isScopeObjectsLoading,
      isWorkersLoading,
      isModelReferenceLoading,
      isPlanningDataLoading,
    ]
  );

  return (
    <PlanningContext.Provider value={value}>{children}</PlanningContext.Provider>
  );
}

export default PlanningProvider;
